import Form, { Field } from '@/components/Form';

import { invitationCrud } from '@/services/firebase/firebase';
import { enqueueSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Link from '../../components/Link';
import { Box } from '@mui/material';
import { hasDateExpire, mergeValuesIntoFields } from '@/utils/helpers';
import { useEffect, useMemo } from 'react';
import { Invitation, User } from '@/types/database';
import { createUserWithInvitation } from '@/services/api';
import useAsync from '@/hooks/useAsync';
import AuthLayout from '../layouts/Auth.layout';
import { Timestamp } from 'firebase/firestore';

const FIELDS: Field[] = [
  { name: 'name', label: 'Name', type: 'text-field', options: { required: true } },
  { name: 'phone', label: 'Phone', type: 'text-field', options: {} },
  { name: 'email', label: 'Email', type: 'text-field', options: { required: true } },
  { name: 'password', label: 'Password', type: 'text-field', options: { type: 'password', required: true } },
];

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitationId') as string;

  const navigate = useNavigate();
  const { error, loading, execute } = useAsync(createUserWithInvitation);
  const {
    data: invitation,
    error: errorInvitation,
    loading: loadingInvitation,
  } = useAsync<Invitation>(() => invitationCrud.read(invitationId), { inmediate: !!invitationId });

  useEffect(() => {
    if (errorInvitation) {
      enqueueSnackbar(errorInvitation?.message, { variant: 'error' });
      navigate('/login');
    } else if (!invitationId || (!loadingInvitation && !invitation)) {
      enqueueSnackbar('Invitation not found', { variant: 'error' });
      navigate('/login');
    } else if (invitation && hasDateExpire((invitation?.invitationToken.expiresAt as Timestamp).toDate())) {
      enqueueSnackbar('Invitation has expired', { variant: 'error' });
      navigate('/login');
    }
  }, [errorInvitation, invitationId, invitation, loadingInvitation]);

  const fields = useMemo(() => mergeValuesIntoFields(invitation?.details, FIELDS), [invitation?.details]);

  const onSubmit = async (values) => {
    await execute(values as User, invitation as Invitation);

    navigate('/login');
    enqueueSnackbar('Registration successful. Please login.', { variant: 'success' });
  };

  return (
    <AuthLayout
      loading={loadingInvitation}
      title="registerTitle"
      description="registerDescription"
      titleProps={{ variant: 'h1', textAlign: 'center' }}
      descriptionProps={{ textAlign: 'center' }}
    >
      <Form fields={fields} error={error?.message} loading={loading} onSubmit={onSubmit} />
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <Link to="/login" variant="subtitle2" sx={{ mt: 3 }}>
          Already have an account? Sign in
        </Link>
      </Box>
    </AuthLayout>
  );
};

export default RegisterPage;
